import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageCircle, X, Headphones, Wrench, Bug } from 'lucide-react';
import { apiServiceWithRefresh } from '../../../utils/api';
import styles from './SupportWidget.module.css';

const UNREAD_POLL_MS = 30000;

const SupportWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const panelRef = useRef(null);
  const queryClient = useQueryClient();

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Poll for unread count via useQuery with refetchInterval
  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['support', 'unreadCount'],
    queryFn: async () => {
      const result = await apiServiceWithRefresh.getUnreadCount();
      if (result.success) {
        return result.unreadCount || 0;
      }
      return 0;
    },
    refetchInterval: UNREAD_POLL_MS,
  });

  // Re-fetch when navigating away from support page (user may have read messages)
  useEffect(() => {
    if (!location.pathname.startsWith('/support')) {
      queryClient.invalidateQueries({ queryKey: ['support', 'unreadCount'] });
    }
  }, [location.pathname, queryClient]);

  const handleOptionClick = (type) => {
    setIsOpen(false);
    if (type === 'bug') {
      navigate('/support?tab=bug-reports');
    } else {
      navigate(`/support?tab=conversations&new=${type}`);
    }
  };

  return (
    <div className={styles.widgetContainer} ref={panelRef}>
      {isOpen && (
        <div className={styles.panel}>
          <div className={styles.panelHeader}>
            <h3 className={styles.panelTitle}>How can we help?</h3>
            <button className={styles.closeBtn} onClick={() => setIsOpen(false)}>
              <X size={18} />
            </button>
          </div>
          <div className={styles.options}>
            <button className={styles.option} onClick={() => handleOptionClick('sales')}>
              <div className={styles.optionIcon} style={{ backgroundColor: '#37A797' }}>
                <Headphones size={20} color="#fff" />
              </div>
              <div className={styles.optionText}>
                <span className={styles.optionTitle}>Sales Inquiry</span>
                <span className={styles.optionDesc}>Questions about plans, pricing, or features</span>
              </div>
            </button>
            <button className={styles.option} onClick={() => handleOptionClick('technical_support')}>
              <div className={styles.optionIcon} style={{ backgroundColor: '#3B574A' }}>
                <Wrench size={20} color="#fff" />
              </div>
              <div className={styles.optionText}>
                <span className={styles.optionTitle}>Technical Support</span>
                <span className={styles.optionDesc}>Help with app issues or how-to questions</span>
              </div>
            </button>
            <button className={styles.option} onClick={() => handleOptionClick('bug')}>
              <div className={styles.optionIcon} style={{ backgroundColor: '#F27C69' }}>
                <Bug size={20} color="#fff" />
              </div>
              <div className={styles.optionText}>
                <span className={styles.optionTitle}>Report a Bug</span>
                <span className={styles.optionDesc}>Something isn't working correctly</span>
              </div>
            </button>
          </div>
        </div>
      )}
      <button
        className={`${styles.fab} ${isOpen ? styles.fabActive : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="Support"
      >
        {isOpen ? <X size={24} /> : <MessageCircle size={24} />}
        {unreadCount > 0 && !isOpen && (
          <span className={styles.unreadBadge}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
    </div>
  );
};

export default SupportWidget;
